import type { KeyboardEvent, MouseEvent } from 'react'

/**
 * WCAG 2.4.1 "Bypass Blocks" — keyboard-only skip link rendered as
 * the first focusable element of every authenticated layout (and the
 * auth-gate loading shell). Visually hidden until focused.
 *
 * Activation is handled by BOTH onClick and onKeyDown(Enter) so the
 * link works reliably in two scenarios:
 *
 * 1. Mouse click: onClick fires directly.
 * 2. Keyboard Enter: browsers synthesise a click from Enter on
 *    focused anchors, but that synthesis can be suppressed if a
 *    window-level keydown listener calls preventDefault() first
 *    (e.g. the queue-keyboard hook in /alerts). Handling Enter in
 *    onKeyDown guarantees the skip action fires regardless of whether
 *    the synthetic click reaches React.
 *
 * Both handlers call the same activate() logic: update the URL hash
 * imperatively via history.replaceState (so React Router is not
 * triggered) then move focus to the landmark.
 */
export function SkipLink() {
  function activate(
    e: MouseEvent<HTMLAnchorElement> | KeyboardEvent<HTMLAnchorElement>,
  ): void {
    const main = document.getElementById('main-content')
    if (!main) return
    e.preventDefault()
    history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}#main-content`,
    )
    main.focus()
  }

  function onKeyDown(e: KeyboardEvent<HTMLAnchorElement>): void {
    if (e.key === 'Enter') {
      activate(e)
    }
  }

  return (
    <a
      href="#main-content"
      onClick={activate}
      onKeyDown={onKeyDown}
      className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-bg focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-fg focus:shadow-lg focus:outline focus:outline-2 focus:outline-primary"
    >
      Skip to main content
    </a>
  )
}
